import { getCountryList, statesByCountry } from "@wayflyer/flyui";

import type { Configuration as KycConfiguration, SupportedCountry as KycSupportedCountry } from "..";

const getPostCodeLabel = (country: KycSupportedCountry): string => {
  const lookup = {
    US: "ZIP code",
    IE: "Eircode",
    GB: "Postcode",
  };
  return lookup[country] ?? "Postal code";
};

export const usKycConfiguration: KycConfiguration = {
  country: "US",
  countryOptions: getCountryList(["US"]),
  postalCodeLabel: "ZIP code",
  stateRegion: {
    label: "State",
    options: statesByCountry["US"],
    requiredBySearch: true,
    searchOptionsLabel: "US State of Registration",
  },
  companyIdentification: {
    label: "Employer Identification Number",
    placeholder: "00-0000000",
    mask: "99-9999999",
  },
  companyVat: false,
  associatedPersonIdentification: {
    label: () => "US Social Security Number",
    placeholder: "000-00-0000",
    mask: "999-99-9999",
    optOutLabel: "This person does not have a US social security number",
  },
  jurisdictionType: "state",
  jurisdictionName: "the United States",
  getPostCodeLabel,
  jurisdictionSpecificFields: {
    phoneNumber: true,
    uboOwnershipPercentage: true,
  },
};

export const usBankingKycConfiguration: KycConfiguration = {
  ...usKycConfiguration,
  enabledProductFlows: ["Banking"],
};

export const gbKycConfiguration: KycConfiguration = {
  country: "GB",
  countryOptions: getCountryList(["GB"]),
  companyIdentification: false,
  stateRegion: {
    label: "County",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "UK National Insurance Number",
    placeholder: "XX123456Y",
    optOutLabel: "This person does not have a national insurance number",
  },
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "1234567890123",
    optOutLabel: "My business does not have a VAT Registration Number",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  postalCodeLabel: "Postcode",
  jurisdictionType: "jurisdiction",
  jurisdictionName: "the United Kingdom",
  getPostCodeLabel,
};

export const ieKycConfiguration: KycConfiguration = {
  country: "IE",
  countryOptions: getCountryList(["IE"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "1234567A",
    optOutLabel: "My business does not have a VAT Registration Number",
  },
  stateRegion: {
    label: "County",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "Irish PPS Number",
    placeholder: "1234567A",
    optOutLabel: "This person does not have a PPS number",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  postalCodeLabel: "Eircode",
  jurisdictionType: "jurisdiction",
  jurisdictionName: "The Republic of Ireland",
  getPostCodeLabel,
};

export const auKycConfiguration: KycConfiguration = {
  country: "AU",
  countryOptions: getCountryList(["AU"]),
  postalCodeLabel: "Postcode",
  stateRegion: {
    label: "Territory",
    requiredBySearch: false,
    options: statesByCountry["AU"],
  },
  companyIdentification: false,
  companyVat: {
    label: "Australian Business Number (ABN)",
    placeholder: "12345678901",
  },
  associatedPersonIdentification: false,
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Australia",
  getPostCodeLabel,
  jurisdictionSpecificConfiguration: {},
};

export const caKycConfiguration: KycConfiguration = {
  country: "CA",
  countryOptions: getCountryList(["CA"]),
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Province",
    requiredBySearch: true,
    options: statesByCountry["CA"],
    searchOptions: [
      { id: "ZZ", text: "Federally incorporated" },
      { id: "NB", text: "New Brunswick" },
      { id: "NL", text: "Newfoundland and Labrador" },
      { id: "NS", text: "Nova Scotia" },
      { id: "PE", text: "Prince Edward Island" },
      { id: "QC", text: "Québec" },
    ],
    searchOptionsLabel: "Canadian province of incorporation",
  },
  companyIdentification: false,
  companyVat: {
    label: "Business Number",
    placeholder: "123456789",
  },
  associatedPersonIdentification: false,
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Canada",
  getPostCodeLabel,
};

export const esKycConfiguration: KycConfiguration = {
  country: "ES",
  countryOptions: getCountryList(["ES"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "B12345678",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Province",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "Spanish Numero de Identificacion Fiscal",
    placeholder: "00000000T",
    optOutLabel: "This person does not have a NIF",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Spain",
  getPostCodeLabel,
};

export const nlKycConfiguration: KycConfiguration = {
  country: "NL",
  countryOptions: getCountryList(["NL"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "123456789A01",
    optOutLabel: "My business does not have a VAT Registration Number",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Province",
    requiredBySearch: false,
    options: statesByCountry["NL"],
  },
  associatedPersonIdentification: false,
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Netherlands",
  getPostCodeLabel,
};

export const beKycConfiguration: KycConfiguration = {
  country: "BE",
  countryOptions: getCountryList(["BE"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "BE123456789",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Province",
    requiredBySearch: false,
    options: statesByCountry["BE"],
  },
  associatedPersonIdentification: {
    label: () => "Belgian National Register number",
    placeholder: "12345678901",
    mask: "99999999999", //11 numbers
    optOutLabel: "This person does not have a National Register number",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Belgium",
  getPostCodeLabel,
};

export const dkKycConfiguration: KycConfiguration = {
  country: "DK",
  countryOptions: getCountryList(["DK"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "DK13585628",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Region",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "Danish CPR Number",
    placeholder: "123456-7890",
    mask: "999999-9999", // ex: 060295-0468
    optOutLabel: "This person does not have a CPR Number",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Denmark",
  getPostCodeLabel,
};

export const seKycConfiguration: KycConfiguration = {
  country: "SE",
  countryOptions: getCountryList(["SE"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "123456789012",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Region",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "Swedish Personnummer",
    placeholder: "123456-7890",
    mask: "999999-9999", // ex: 640507-7709
    optOutLabel: "This person does not have a Personnummer",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Sweden",
  getPostCodeLabel,
};

export const nzKycConfiguration: KycConfiguration = {
  country: "NZ",
  countryOptions: getCountryList(["NZ"]),
  companyIdentification: false,
  companyVat: {
    label: "NZ GST/IRD Number",
    placeholder: "12345678",
  },
  postalCodeLabel: "Postal code",
  stateRegion: {
    label: "Region",
    requiredBySearch: false,
  },
  associatedPersonIdentification: false,
  jurisdictionType: "jurisdiction",
  jurisdictionName: "New Zealand",
  getPostCodeLabel,
};

export const deKycConfiguration: KycConfiguration = {
  country: "DE",
  countryOptions: getCountryList(["DE"]),
  companyIdentification: {
    label: "Company Tax ID (Steuernummer)",
    placeholder: "123/456/7890(0)(00)",
  },
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "DE123456789",
    optOutLabel: "My business does not have a VAT Registration Number",
  },
  postalCodeLabel: "Postcode",
  stateRegion: {
    label: "State",
    requiredBySearch: false,
  },
  associatedPersonIdentification: {
    label: () => "German Steuer-Identifikationsnummer",
    placeholder: "99 999 999 999",
    mask: "99 999 999 999",
    optOutLabel: "This person does not have a German Steuer-Identifikationsnummer",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "Germany",
  getPostCodeLabel,
  jurisdictionSpecificFields: {
    nationality: true,
    uboOwnershipPercentage: true,
    powerOfAttorney: true,
  },
  jurisdictionSpecificConfiguration: {
    areMultipleSignersAllowed: true,
    getSigneeChangeParams: (value) => ({
      is_signing_contract: value?.isSignee,
      power_of_attorney: value?.powerOfAttorney,
    }),
    verifyIdentityText: {
      requiresInput: {
        title: "Start identity verification",
        text: "Before starting you will need a valid government-issued photo ID document.",
      },
    },
  },
};

export const frKycConfiguration: KycConfiguration = {
  country: "FR",
  countryOptions: getCountryList(["FR"]),
  companyIdentification: false,
  companyVat: {
    label: "VAT Registration Number",
    placeholder: "1234567890123",
  },
  postalCodeLabel: "Postcode",
  stateRegion: {
    label: "State",
    requiredBySearch: false,
    options: statesByCountry["FR"],
  },
  associatedPersonIdentification: {
    label: () => "French Numéro Fiscal de Référence",
    placeholder: "99 99 999 999 999", //ex: 12 36 574 261 809
    mask: "99 99 999 999 999",
    optOutLabel: "This person does not French NIF",
  },
  associatedPersonPlaceOfBirth: {
    countryOfBirth: { required: true },
    cityOfBirth: { required: true },
  },
  jurisdictionType: "jurisdiction",
  jurisdictionName: "France",
  personNationality: true,
  getPostCodeLabel,
};
